import { Table as AntTable, type TableProps as AntdTableProps } from "antd/es";
import type { SizeType } from "antd/es/config-provider/SizeContext";
import type { ColumnGroupType } from "antd/es/table";
import get from "lodash/get";
import isString from "lodash/isString";
import React from "react";

import { IconFilterFilled, IconSearchOutlined } from "~/components/icon";
import { cv } from "~/tools/class-variance";
import { isArray } from "~/tools/is-array";
import type { Object } from "~/tools/object";

import { LoadingSpinner } from "../loading-spinner/LoadingSpinner";

import { SearchFilterDropdown, SelectFilterDropdown } from "./TableFilters";
import { type ColumnsType, isColumnType } from "./TableTypes";
import styles from "./table.module.css";

const variants = cv(styles.table, {
	variants: {
		withoutHover: {
			true: styles.withoutHover,
		},
		noHeaderVerticalBorders: {
			true: styles.noHeaderVerticalBorders,
		},
		noHorizontalBorders: {
			true: styles.noHorizontalBorders,
		},
		noLastHorizontalBorder: {
			true: styles.noLastHorizontalBorder,
		},
		outerBorders: {
			true: styles.outerBorders,
		},
		dimHeaderAndBorders: {
			true: styles.dimHeaderAndBorders,
		},

		size: {
			compact: styles.compact,
		},
	},
});

type TableBorders = "header-vertical-short" | "rows-horizontal" | "rows-last-horizontal" | "outer";

export interface RowSelectionProps<T extends Object> {
	onChange?: (selectedRowKeys: Array<string> | Array<number>, selectedRows: Array<T>) => void;
}

export interface TableProps<T extends Object>
	extends Pick<
		AntdTableProps<T>,
		| "className"
		| "components"
		| "sticky"
		| "scroll"
		| "showSorterTooltip"
		| "title"
		| "pagination"
		| "rowClassName"
		| "rowKey"
		| "expandable"
		| "onChange"
		| "onRow"
		| "tableLayout"
	> {
	size?: "compact" | SizeType;
	data?: ReadonlyArray<T>;
	columns: ColumnsType<T>;
	rowSelection?: RowSelectionProps<T>;
	borders?: TableBorders | Array<TableBorders>;
	dimHeaderAndBorders?: boolean;
	withHover?: boolean;
	loading?: boolean;
}

/**
 * @deprecated Use ~/components/table instead
 */
export const Table: <TDate extends Object>(props: TableProps<TDate>) => ReturnType<React.FC> = React.memo(
	({
		columns,
		data,
		size,
		borders = ["header-vertical-short", "rows-horizontal", "rows-last-horizontal"],
		withHover = true,
		dimHeaderAndBorders,
		sticky,
		className,
		loading = false,
		...props
	}) => {
		const bordersFlat = [borders].flat();
		const transformedColumns = React.useMemo(() => transformColumns(columns), [columns]);
		const stickyTopPosition =
			sticky === true ? 0 : sticky && sticky.getContainer == null ? (sticky.offsetScroll ?? 0) : null;

		return (
			<AntTable
				{...props}
				className={variants({
					className,
					withoutHover: !withHover,
					noHeaderVerticalBorders: !bordersFlat.includes("header-vertical-short"),
					noHorizontalBorders: !bordersFlat.includes("rows-horizontal"),
					noLastHorizontalBorder: !bordersFlat.includes("rows-last-horizontal"),
					outerBorders: bordersFlat.includes("outer"),
					dimHeaderAndBorders,
					size: size === "compact" ? size : undefined,
				})}
				// implement custom sticky css only based behavior because by default antd introduces
				// two separate tables for header and body, which causes issues with shrunk columns
				columns={transformedColumns}
				dataSource={data}
				loading={{
					indicator: <LoadingSpinner />,
					spinning: loading,
				}}
				size={size === "compact" ? undefined : size}
				sticky={stickyTopPosition == null ? sticky : undefined}
			/>
		);
	},
);

const transformColumns = <T extends Object>(columns: ColumnsType<T>): NonNullable<AntdTableProps<T>["columns"]> => {
	return columns.map((column) => {
		if (!isColumnType(column)) {
			return column satisfies ColumnGroupType<T>; // no filterMode, safe to pass through
		}

		// Strip filterMode — antd only accepts "menu" | "tree"; our custom values are handled below
		const { filterMode, ...rest } = column;

		if (filterMode === "search" && supportsCustomFilter(column.dataIndex)) {
			const dataIndex = column.dataIndex as string;
			return {
				filterDropdown: SearchFilterDropdown,
				filterIcon: (filtered: boolean) => (
					<IconSearchOutlined
						className={filtered ? "text-primary-600" : "text-gray-600"}
						data-testid={`table-search-filer-icon-${dataIndex}`}
					/>
				),
				onFilter: (search: string, record: T): boolean => {
					const value = get(record, dataIndex, "");
					return value ? value.toString().toLowerCase().includes(search.toLowerCase()) : false;
				},
				...rest,
			};
		}

		if (filterMode === "select") {
			return {
				filterDropdown: SelectFilterDropdown,
				filterIcon: (filtered: boolean) => (
					<IconFilterFilled
						className={filtered ? "text-primary-600" : "text-gray-600"}
						data-testid="table-select-filter-icon"
					/>
				),
				...rest,
			};
		}

		return rest;
	});
};

const supportsCustomFilter = (dataIndex: unknown): dataIndex is string | Array<string> =>
	isString(dataIndex) || isArray(dataIndex);
